/**
 * Localized cornerstone data loader.
 *
 * Each non-default-locale file lives at /src/data/cornerstones/{lang}.js
 * (generated by scripts/translate_cornerstones.py). Missing keys fall back
 * to the Dutch source so pages still render.
 */
import CORNERSTONES_NL from "@/data/cornerstones/nl";

const LOCALES = [
  "en", "de", "fr", "es", "it", "pt", "pl", "sv", "da", "fi", "no",
  "cs", "sk", "hu", "ro", "bg", "el", "et", "lv", "lt", "hr", "sl", "ga",
  "mt", "zh", "ar",
];

const cache = { nl: CORNERSTONES_NL };

LOCALES.forEach((lang) => {
  try {
    // eslint-disable-next-line global-require
    const mod = require(`@/data/cornerstones/${lang}.js`);
    cache[lang] = mod.CORNERSTONES || mod.default || CORNERSTONES_NL;
  } catch (e) {
    cache[lang] = CORNERSTONES_NL;
  }
});

/** Deep-merge: override wins for primitives; recurse for objects/arrays; falls back to base when override is missing. */
function mergeWithFallback(base, override) {
  // Override missing entirely → use base
  if (override === undefined || override === null) return base;
  // Both arrays → element-wise merge
  if (Array.isArray(base) && Array.isArray(override)) {
    return override.map((item, i) =>
      typeof item === "object" && item !== null && base[i] && typeof base[i] === "object"
        ? mergeWithFallback(base[i], item)
        : (item !== undefined ? item : base[i])
    );
  }
  // Both plain objects → key-wise merge
  if (
    base && typeof base === "object" && !Array.isArray(base) &&
    override && typeof override === "object" && !Array.isArray(override)
  ) {
    const result = { ...base };
    Object.keys(override).forEach((k) => {
      result[k] = mergeWithFallback(base[k], override[k]);
    });
    return result;
  }
  // Primitive or mismatched types → override wins
  return override;
}

/** Normalize locale code: strip region suffix (e.g. "en-US" → "en"). */
function normalize(localeCode) {
  if (!localeCode || typeof localeCode !== "string") return "nl";
  return localeCode.toLowerCase().split("-")[0];
}

/** Get a cornerstone page's content for a locale, falling back to NL per-field. */
export function getCornerstone(slug, localeCode) {
  const code = normalize(localeCode);
  const localeBundle = cache[code] || CORNERSTONES_NL;
  const nlPage = CORNERSTONES_NL[slug];
  const localePage = localeBundle[slug];
  return mergeWithFallback(nlPage, localePage);
}

/** Get the localized UI chrome strings shared by all cornerstones. */
export function getCornerstoneUi(localeCode) {
  const code = normalize(localeCode);
  const localeBundle = cache[code] || CORNERSTONES_NL;
  return mergeWithFallback(CORNERSTONES_NL.ui, localeBundle.ui);
}

/** True when this locale has no translated bundle (showing NL fallback). */
export function isCornerstoneFallback(localeCode) {
  const code = normalize(localeCode);
  if (!code || code === "nl") return false;
  return !cache[code] || cache[code] === CORNERSTONES_NL;
}

export default getCornerstone;
